<template>
  <!-- FT-1487: THE CENTRAL CROSS AREA — five reserved slots laid over the
       backdrop's own painted panels (the Cart's five black frames first). The
       middle slot carries the player counts (and, later, the end-game winner
       disc); the other four are reserved for a later slice to dress. Replaces
       the clocktower's retired centre disc as the town's anchor.

       Pointer-transparent geometry: it sits behind the seat ring and the
       centre content, never eats a click. Positions/size ride the --cross-*
       vars (golem/townLayout, dialled per backdrop in the town-layout lab). -->
  <div id="town-cross" :class="{ outline: layout.outline }" aria-hidden="true">
    <div class="tc-slot tc-top"></div>
    <div class="tc-slot tc-left"></div>
    <div class="tc-slot tc-right"></div>
    <div class="tc-slot tc-bottom"></div>
    <div class="tc-slot tc-mid">
      <div class="tc-counts">
        <span class="tc-total">{{ players.length }}</span>
        <span class="tc-comp" v-if="comp">
          <b class="tf">{{ comp[0] }}</b>
          <b class="ou">{{ comp[1] }}</b>
          <b class="mi">{{ comp[2] }}</b>
          <b class="de">{{ comp[3] }}</b>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import { SETUP_TABLE } from "../golem/composition";
import { townLayoutState } from "../golem/townLayout";

export default {
  data() {
    return { layout: townLayoutState };
  },
  computed: {
    ...mapState("players", ["players"]),
    ...mapGetters("players", ["nonTravelers"]),
    /**
     * The setup composition [townsfolk, outsiders, minions, demons] for this
     * town's non-traveller count — the same numbers the build dial shows,
     * from the one SETUP_TABLE source (golem/composition). Null under five,
     * where the official table has no row.
     */
    comp() {
      return SETUP_TABLE[this.nonTravelers] || null;
    },
  },
};
</script>

<style scoped lang="scss">
@import "../vars.scss";

/* One pointer-transparent layer centred on the viewport. It sits below the
   seat ring and centre content (both paint later in App.vue's tree) and above
   the backdrop, so the middle slot's counts read on the middle panel while
   the seats stand clear in front. */
#town-cross {
  position: fixed;
  inset: 0;
  // Below the seat ring (its lis run z-index 1..20) and the centre content,
  // above the backdrop. `.circle` is not a stacking context (relative, no
  // transform), so the seats compare at the root: z-index 1 here ties the
  // lowest seat and loses to it on DOM order (TownSquare paints after this).
  z-index: 1;
  pointer-events: none;
}

/* Each slot's centre = viewport centre + (cross offset + its own base offset
   scaled by --cross-spread), in vh; its size = the measured panel size scaled
   by --cross-slot-scale. The base numbers are the Cart's five panels measured
   off the pixels of background-lots-cart.jpg (natural 1672x916, drawn at
   100vh tall on a ≤1.824-aspect viewport): panels ~9.5vh wide, 17.2vh tall;
   the outer four offset ±25.7 vh (top/bottom) and ±16.8 vh (left/right) from
   the middle one, which itself sits ~0.7vh right of and ~4vh above the
   viewport centre (the backdrop's +7px bake, and the cart's panels riding a
   touch high). The lab nudges all of it, per backdrop. */
.tc-slot {
  position: absolute;
  transform: translate(-50%, -50%);
  width: calc(9.5 * var(--cross-slot-scale, 1) * 1vh);
  height: calc(17.2 * var(--cross-slot-scale, 1) * 1vh);
}

.tc-top {
  left: calc(50% + var(--cross-x, 0.7) * 1vh);
  top: calc(50% + (var(--cross-y, -4) + -25.7 * var(--cross-spread, 1)) * 1vh);
}
.tc-bottom {
  left: calc(50% + var(--cross-x, 0.7) * 1vh);
  top: calc(50% + (var(--cross-y, -4) + 25.8 * var(--cross-spread, 1)) * 1vh);
}
.tc-left {
  left: calc(
    50% + (var(--cross-x, 0.7) + -16.7 * var(--cross-spread, 1)) * 1vh
  );
  top: calc(50% + var(--cross-y, -4) * 1vh);
}
.tc-right {
  left: calc(50% + (var(--cross-x, 0.7) + 16.9 * var(--cross-spread, 1)) * 1vh);
  top: calc(50% + var(--cross-y, -4) * 1vh);
}
.tc-mid {
  left: calc(50% + var(--cross-x, 0.7) * 1vh);
  top: calc(50% + var(--cross-y, -4) * 1vh);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* The reserved four are invisible unless the lab asks for their outlines. */
#town-cross.outline .tc-slot {
  outline: 1px dashed rgba(var(--reader-rgb, 205, 156, 19), 0.7);
  outline-offset: -1px;
}

/* THE MIDDLE SLOT — the player counts, on the middle panel. Minimal chrome:
   the panel behind it is already a dark frame. */
.tc-counts {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2em;
  color: #e8ddc4;
  text-shadow: 0 2px 6px rgba(0, 0, 0, 0.9);
  line-height: 1;
}
.tc-total {
  font-size: calc(6 * var(--cross-slot-scale, 1) * 1vh);
  font-weight: 700;
  color: rgb(var(--reader-rgb, 205, 156, 19));
}
.tc-comp {
  display: flex;
  gap: 0.45em;
  font-size: calc(2.1 * var(--cross-slot-scale, 1) * 1vh);
  b {
    font-weight: 600;
  }
  .tf {
    color: #3d78c9;
  }
  .ou {
    color: #4aa3c9;
  }
  .mi {
    color: #d0682f;
  }
  .de {
    color: $demon;
  }
}
</style>
